/**
 * TemplateCreator, hosted: read a 03 drawing, answer the project questions, and get the reviewed
 * .ae01.json package the AutoCAD AE01 command renders. The layout follows the desktop app - input
 * cards on the left, Build Review and Summary on the right - and the drawing is read in the
 * browser, so it never leaves the machine.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { userFrom } from "@plan-forge/entra-auth";
import {
  BLANK_ATTRIBUTES_ADVICE,
  blankAttributeLines,
  blankAttributeReminders,
  isPalmBeachCounty,
  serializePackage,
  type BuildManifest,
  type DwgScanUpload,
  type NetworkMode,
  type ProjectType,
} from "@plan-forge/templatecreator-contract";
import { authHeader, authRequired, initialAuthState, signIn, signOut, type AuthState } from "./auth.ts";
import { ApiError } from "./api.ts";
import { scanDwgFile } from "./dwg-scan.ts";
import { buildManifest } from "./templatecreator-api.ts";
import {
  EMPTY_ZONE,
  INITIAL_FORM,
  filterZones,
  packageFileName,
  reviewProgress,
  reviewSections,
  summaryText,
  toFacts,
  withCutover,
  withProjectType,
  withTwoWaySystem,
  withZone,
  withZones,
  type FormState,
} from "./templatecreator-view.ts";
import { SignIn } from "./SignIn.tsx";
import "./templatecreator.css";

interface SaveFileHandle {
  createWritable(): Promise<{ write(data: string): Promise<void>; close(): Promise<void> }>;
}

interface DrawingHandle {
  getFile(): Promise<File>;
}

type OpenFilePicker = (options: {
  id: string;
  types: { description: string; accept: Record<string, string[]> }[];
}) => Promise<DrawingHandle[]>;

/** Chrome and Edge return a handle for the picked file, which lets the save dialog open in that same folder. */
function openPicker(): OpenFilePicker | null {
  const picker = (window as unknown as { showOpenFilePicker?: OpenFilePicker }).showOpenFilePicker;
  return typeof picker === "function" ? picker.bind(window) : null;
}

type SaveFilePicker = (options: {
  suggestedName: string;
  id: string;
  startIn?: DrawingHandle;
  types: { description: string; accept: Record<string, string[]> }[];
}) => Promise<SaveFileHandle>;

/** Chrome and Edge let the person choose the folder, so the package can be saved beside the 03 file. */
function savePicker(): SaveFilePicker | null {
  const picker = (window as unknown as { showSaveFilePicker?: SaveFilePicker }).showSaveFilePicker;
  return typeof picker === "function" ? picker.bind(window) : null;
}

/** Built by TemplateCreator/App/connector/package-connector.ps1 and shipped with the site from apps/ui/public. */
const CONNECTOR_DOWNLOAD = "/connector/AETemplateCreator-connector.zip";

function isCancelled(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function download(manifest: BuildManifest, projectName: string): void {
  const url = URL.createObjectURL(new Blob([serializePackage(manifest)], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = packageFileName(projectName);
  link.click();
  URL.revokeObjectURL(url);
}

/** Shown in two places; only the 03 drawing card's copy is announced, so a screen reader reads it once. */
function BlankAttributesNotice({ lines, announce }: { readonly lines: readonly string[]; readonly announce: boolean }) {
  return (
    <div className="templatecreator__problem" role={announce ? "alert" : undefined}>
      <p>
        This drawing has equipment with a blank NETWORK_ID or AMP_ID, so a template cannot be created from it yet:
      </p>
      <ul>
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <p>{BLANK_ATTRIBUTES_ADVICE}</p>
    </div>
  );
}

function scanMessage(scan: DwgScanUpload | null): string {
  if (scan === null) return "not scanned";
  return `${Object.keys(scan.blockCounts).length} block name(s), ${scan.zoneNames.length} zone(s)`;
}

function ZoneBulk({ label, onAll, onNone }: { label: string; onAll: () => void; onNone: () => void }) {
  return (
    <span className="tc-zones__bulk">
      <button type="button" className="tc-link" aria-label={`Mark ${label} on every zone shown`} onClick={onAll}>
        all
      </button>
      <button type="button" className="tc-link" aria-label={`Clear ${label} on every zone shown`} onClick={onNone}>
        none
      </button>
    </span>
  );
}

export function TemplateCreatorApp() {
  const [auth, setAuth] = useState<AuthState>({ kind: authRequired ? "loading" : "disabled" });
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [dwgName, setDwgName] = useState("");
  const [dwgHandle, setDwgHandle] = useState<DrawingHandle | null>(null);
  const [zoneFilter, setZoneFilter] = useState("");
  const [scan, setScan] = useState<DwgScanUpload | null>(null);
  const [manifest, setManifest] = useState<BuildManifest | null>(null);
  const [completed, setCompleted] = useState<ReadonlySet<string>>(new Set());
  const [tab, setTab] = useState<"review" | "summary">("review");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState<"scan" | "build" | null>(null);

  useEffect(() => {
    document.title = "Template Creator - DesignTools";
  }, []);

  useEffect(() => {
    if (!authRequired) return;
    void initialAuthState().then(setAuth);
  }, []);

  const headers = useCallback(
    () => authHeader(auth, (session) => setAuth({ kind: "signed-in", session, user: userFrom(session) })),
    [auth],
  );

  const patch = useCallback((changes: Partial<FormState>) => setForm((current) => ({ ...current, ...changes })), []);

  const handleDrawing = useCallback(async (file: File) => {
    setProblem(null);
    setManifest(null);
    setBusy("scan");
    try {
      setScan(await scanDwgFile(file));
      setDwgName(file.name);
    } catch (error) {
      setScan(null);
      setDwgName("");
      setProblem(error instanceof ApiError ? error.errors.join("; ") : "The drawing could not be read.");
    } finally {
      setBusy(null);
    }
  }, []);

  const chooseDrawing = useCallback(async () => {
    const picker = openPicker();
    if (picker === null) return;
    try {
      const [handle] = await picker({
        id: "templatecreator-03",
        types: [{ description: "AutoCAD drawing", accept: { "application/acad": [".dwg"] } }],
      });
      if (handle === undefined) return;
      setDwgHandle(handle);
      await handleDrawing(await handle.getFile());
    } catch (error) {
      if (!isCancelled(error)) setProblem("The drawing could not be opened.");
    }
  }, [handleDrawing]);

  const blankLines = useMemo(() => (scan === null ? [] : blankAttributeLines(scan.blankAttributes)), [scan]);
  const blankReminders = useMemo(() => (scan === null ? [] : blankAttributeReminders(scan.blankAttributes)), [scan]);

  const createTemplate = useCallback(async () => {
    if (form.projectName.trim() === "") {
      setProblem("Project name is required.");
      return;
    }
    // Checked before the save dialog opens, so a drawing that cannot be built never gets as far as
    // choosing a file name.
    if (blankLines.length > 0) {
      setProblem("No template was created: fill in the blank NETWORK_ID and AMP_ID attributes listed under 03 Drawing first.");
      return;
    }
    setProblem(null);
    // The save dialog has to open from the click itself: a browser only allows it shortly after
    // one, and the build below is a network call.
    const picker = savePicker();
    let handle: SaveFileHandle | null = null;
    if (picker !== null) {
      try {
        handle = await picker({
          suggestedName: packageFileName(form.projectName.trim()),
          id: "templatecreator-package",
          ...(dwgHandle === null ? {} : { startIn: dwgHandle }),
          types: [{ description: "AE 01 package", accept: { "application/json": [".json"] } }],
        });
      } catch (error) {
        if (isCancelled(error)) return;
        handle = null;
      }
    }
    setBusy("build");
    try {
      const response = await buildManifest(toFacts(form, scan, scan === null ? "" : dwgName), headers);
      setManifest(response.manifest);
      setTab("review");
      if (handle === null) {
        download(response.manifest, form.projectName.trim());
      } else {
        const writable = await handle.createWritable();
        await writable.write(serializePackage(response.manifest));
        await writable.close();
      }
    } catch (error) {
      setProblem(error instanceof ApiError ? error.errors.join("; ") : "The template could not be built.");
    } finally {
      setBusy(null);
    }
  }, [form, scan, dwgName, dwgHandle, headers, blankLines]);

  const sections = useMemo(() => reviewSections(manifest?.review_items ?? []), [manifest]);
  const progress = reviewProgress(sections, completed);

  const toggleCompleted = (key: string, on: boolean) =>
    setCompleted((current) => {
      const next = new Set(current);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  if (auth.kind === "loading") return <div className="booting">Checking your sign-in...</div>;
  if (auth.kind === "signed-out") {
    return <SignIn reason={auth.reason} onSignIn={() => void signIn()} />;
  }

  const newConstruction = form.projectType === "New Construction";
  const palmBeach = isPalmBeachCounty(form.district, form.address);

  return (
    <main className="templatecreator">
      <header className="templatecreator__header">
        <div>
          <a className="templatecreator__back" href="/">
            &larr; DesignTools
          </a>
          <h1 className="templatecreator__title">AE Template Creator</h1>
          <p className="templatecreator__subtitle">Scan the 03 drawing and build the AE 01 template package.</p>
        </div>
        {auth.kind === "signed-in" && (
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              signOut();
              setAuth({ kind: "signed-out" });
            }}
          >
            Sign out
          </button>
        )}
      </header>

      <div className="tc">
        <div className="tc__inputs">
          <section className="tc-card">
            <h2 className="tc-card__title">Project identity</h2>
            <div className="field">
              <label htmlFor="tc-project-name">Project name</label>
              <input id="tc-project-name" type="text" value={form.projectName} onChange={(e) => patch({ projectName: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="tc-district">District</label>
              <input id="tc-district" type="text" value={form.district} onChange={(e) => patch({ district: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="tc-address">Address</label>
              <input id="tc-address" type="text" value={form.address} onChange={(e) => patch({ address: e.target.value })} />
            </div>
          </section>

          <section className="tc-card">
            <h2 className="tc-card__title">03 drawing</h2>
            <div className="field">
              <label htmlFor="tc-dwg">Existing drawing (.dwg)</label>
              {openPicker() !== null ? (
                <button type="button" id="tc-dwg" className="btn" onClick={() => void chooseDrawing()}>
                  {dwgName === "" ? "Choose 03 drawing..." : "Choose a different drawing..."}
                </button>
              ) : (
                <input
                  id="tc-dwg"
                  type="file"
                  accept=".dwg"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file !== undefined) {
                      setDwgHandle(null);
                      void handleDrawing(file);
                    }
                  }}
                />
              )}
            </div>
            {busy === "scan" && <p className="templatecreator__hint">Reading the drawing...</p>}
            {scan !== null && (
              <p className="templatecreator__hint">
                {dwgName}: {scanMessage(scan)}. Read in your browser - the drawing is not uploaded.
              </p>
            )}
            {blankLines.length > 0 && <BlankAttributesNotice lines={blankLines} announce />}
            {blankReminders.map((reminder) => (
              <p key={reminder} className="templatecreator__hint">
                {reminder}
              </p>
            ))}
            {scan !== null && scan.anonymousBlockReferences > 0 && (
              <p className="templatecreator__hint">
                {scan.anonymousBlockReferences} dynamic-block reference(s) could not be named and were not counted.
              </p>
            )}
          </section>

          <section className="tc-card">
            <h2 className="tc-card__title">Project Type</h2>
            <div className="tc-choice">
              {(["Retrofit", "New Construction"] as const satisfies readonly ProjectType[]).map((type) => (
                <label key={type}>
                  <input type="radio" name="tc-project-type" checked={form.projectType === type} onChange={() => setForm((f) => withProjectType(f, type))} />
                  {type}
                </label>
              ))}
            </div>
          </section>

          <section className="tc-card">
            <h2 className="tc-card__title">Room Combine?</h2>
            <p className="templatecreator__hint">Number of combined-room systems needing a riser (0 if none).</p>
            <div className="field">
              <label htmlFor="tc-f008">MS-700 (F008)</label>
              <input id="tc-f008" className="tc-number" type="number" min={0} max={20} value={form.roomCombineMs700} onChange={(e) => patch({ roomCombineMs700: Number(e.target.value) || 0 })} />
            </div>
            <div className="field">
              <label htmlFor="tc-f009">70V Classroom System (F009)</label>
              <input id="tc-f009" className="tc-number" type="number" min={0} max={20} value={form.roomCombine70v} onChange={(e) => patch({ roomCombine70v: Number(e.target.value) || 0 })} />
            </div>
          </section>

          <section className="tc-card">
            <h2 className="tc-card__title">Two-Way System</h2>
            <p className="templatecreator__hint">
              {palmBeach
                ? "Palm Beach County: the two-way system comes from the MS-720s on the 03 drawing."
                : "Selects H002-H007, paired with the network connection type. Leave on None if there is no two-way system."}
            </p>
            <div className="tc-choice">
              {([["", "None"], ["WPA", "WPA"], ["ITC", "ITC"]] as const).map(([value, label]) => (
                <label key={label}>
                  <input type="radio" name="tc-two-way" disabled={palmBeach} checked={(palmBeach ? "" : form.twoWaySystemType) === value} onChange={() => setForm((f) => withTwoWaySystem(f, value))} />
                  {label}
                </label>
              ))}
            </div>
            <label className="templatecreator__checkbox">
              <input type="checkbox" disabled={palmBeach || form.twoWaySystemType === "WPA"} checked={!palmBeach && form.twoWaySystemStrobe} onChange={(e) => patch({ twoWaySystemStrobe: e.target.checked })} />
              Strobe
            </label>
          </section>

          <section className="tc-card">
            <h2 className="tc-card__title">Network Connection Type</h2>
            <div className="tc-choice">
              {(["Existing", "AE Provided"] as const satisfies readonly NetworkMode[]).map((mode) => (
                <label key={mode}>
                  <input type="radio" name="tc-network" disabled={newConstruction && mode === "AE Provided"} checked={form.networkMode === mode} onChange={() => patch({ networkMode: mode })} />
                  {mode}
                </label>
              ))}
            </div>
            {newConstruction && <p className="templatecreator__hint">New Construction network drops are assumed to be provided by the data contractor.</p>}
          </section>

          <section className="tc-card">
            <h2 className="tc-card__title">Other</h2>
            <label className="templatecreator__checkbox">
              <input type="checkbox" checked={form.poeSurgeProtectors} onChange={(e) => patch({ poeSurgeProtectors: e.target.checked })} />
              PoE+ Surge Protectors (D014)
            </label>
            <label className="templatecreator__checkbox">
              <input type="checkbox" checked={form.safeDongles} onChange={(e) => patch({ safeDongles: e.target.checked })} />
              SAFE Dongles (C007 + Scope of Work badges)
            </label>
            <label className="templatecreator__checkbox">
              <input type="checkbox" checked={form.cutover} onChange={(e) => setForm((f) => withCutover(f, e.target.checked))} />
              Cutover
            </label>
          </section>

          <section className="tc-card">
            <h2 className="tc-card__title">Loudspeaker zones</h2>
            {scan === null || scan.zoneNames.length === 0 ? (
              <p className="templatecreator__hint">Zones appear here once the 03 drawing is read.</p>
            ) : (
              <>
                <p className="templatecreator__hint">
                  {scan.zoneNames.length} zones. Mark what each zone's loudspeaker run gets before the speakers continue.
                </p>
                <input
                  className="tc-zones__filter"
                  type="search"
                  aria-label="Filter zones"
                  placeholder="Filter zones"
                  value={zoneFilter}
                  onChange={(e) => setZoneFilter(e.target.value)}
                />
                <div className="tc-zones__scroll">
                  <table className="tc-zones">
                    <thead>
                      <tr>
                        <th scope="col">Zone</th>
                        <th scope="col">
                          Surge protector
                          <ZoneBulk
                            label="surge protector"
                            onAll={() => setForm((f) => withZones(f, filterZones(scan.zoneNames, zoneFilter), "surgeProtector", true))}
                            onNone={() => setForm((f) => withZones(f, filterZones(scan.zoneNames, zoneFilter), "surgeProtector", false))}
                          />
                        </th>
                        {form.cutover && (
                          <th scope="col">
                            Terminal block
                            <ZoneBulk
                              label="terminal block"
                              onAll={() => setForm((f) => withZones(f, filterZones(scan.zoneNames, zoneFilter), "terminalBlock", true))}
                              onNone={() => setForm((f) => withZones(f, filterZones(scan.zoneNames, zoneFilter), "terminalBlock", false))}
                            />
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filterZones(scan.zoneNames, zoneFilter).map((zone) => {
                        const choice = form.zones[zone] ?? EMPTY_ZONE;
                        return (
                          <tr key={zone}>
                            <th scope="row">{zone}</th>
                            <td>
                              <input type="checkbox" aria-label={`${zone}: surge protector`} checked={choice.surgeProtector} onChange={(e) => setForm((f) => withZone(f, zone, { surgeProtector: e.target.checked }))} />
                            </td>
                            {form.cutover && (
                              <td>
                                <input type="checkbox" aria-label={`${zone}: terminal block`} checked={choice.terminalBlock} onChange={(e) => setForm((f) => withZone(f, zone, { terminalBlock: e.target.checked }))} />
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {filterZones(scan.zoneNames, zoneFilter).length === 0 && <p className="templatecreator__hint">No zone matches "{zoneFilter}".</p>}
              </>
            )}
          </section>

          {problem !== null && (
            <p className="templatecreator__problem" role="alert">
              {problem}
            </p>
          )}
          <div className="tc__actions">
            <button type="button" className="btn btn--primary" disabled={busy !== null} onClick={() => void createTemplate()}>
              {busy === "build" ? "Creating..." : "Create Template"}
            </button>
          </div>
        </div>

        <div className="tc__review">
          <div className="tc-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "review"} className="tc-tab" onClick={() => setTab("review")}>
              Build Review
            </button>
            <button type="button" role="tab" aria-selected={tab === "summary"} className="tc-tab" onClick={() => setTab("summary")}>
              Summary
            </button>
          </div>

          {manifest === null && blankLines.length > 0 ? (
            <div role="tabpanel">
              <h2 className="tc-card__title">Review Items</h2>
              <BlankAttributesNotice lines={blankLines} announce={false} />
            </div>
          ) : manifest === null ? (
            <p className="templatecreator__hint">
              Enter the project information and click Create Template. That analyzes the project and creates the .ae01.json
              package used by AutoCAD. Save it in the same folder as the 03 drawing, then in AutoCAD type AE01 and select it. If the 03 file is not beside the package or the open drawing, AE01 asks you to select it.{" "}
              New to this, or AE01 says the connector is out of date? <a href={CONNECTOR_DOWNLOAD} download>Download the AutoCAD connector</a>, close AutoCAD, and run Install.bat.
            </p>
          ) : tab === "review" ? (
            <div role="tabpanel">
              <h2 className="tc-card__title">Review Items</h2>
              <p className="tc-progress__label">
                {manifest.required_connector_version
                  ? `Needs AutoCAD connector v${manifest.required_connector_version} or newer. AE01 stops with a message if the installed one is older. `
                  : ""}
                <a href={CONNECTOR_DOWNLOAD} download>Download connector</a>
              </p>
              <p className="tc-progress__label">{progress.total === 0 ? "No review items" : `${progress.done} / ${progress.total} completed`}</p>
              <progress className="tc-progress" max={Math.max(progress.total, 1)} value={progress.done} />
              {sections.map((section) => (
                <div key={section.group} className="tc-group">
                  <h3 className="tc-group__label">{section.group.toUpperCase()}</h3>
                  {section.items.map((item) => (
                    <label key={item.key} className={item.severity === "review" ? "tc-item" : "tc-item tc-item--warning"}>
                      <input type="checkbox" checked={completed.has(item.key)} onChange={(e) => toggleCompleted(item.key, e.target.checked)} />
                      <span className="tc-item__text">
                        <span className="tc-item__done">Completed</span> {item.message}
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <pre className="tc-summary" role="tabpanel">
              {summaryText(manifest, scanMessage(scan))}
            </pre>
          )}
        </div>
      </div>
    </main>
  );
}
